/**
 * Product Image Zoom System
 * Hover Zoom, Lens Zoom, Lightbox Gallery, Pinch-to-Zoom
 * Lean Theme
 */
'use strict';

const LeanZoom = (() => {
  let config = {};
  let lightbox = {};
  let currentSlide = 0;
  let lbZoomLevel = 1;
  let lbPanX = 0;
  let lbPanY = 0;
  let isDragging = false;
  let dragStart = { x: 0, y: 0 };
  let touchStartDist = 0;
  let touchStartZoom = 1;
  let swipeStartX = 0;

  function init(sectionId) {
    var configEl = document.querySelector('[data-zoom-config="' + sectionId + '"]');
    if (!configEl) return;

    config = {
      sectionId: sectionId,
      type: configEl.dataset.zoomType || 'hover',
      level: parseFloat(configEl.dataset.zoomLevel) || 2,
      lightbox: configEl.dataset.zoomLightbox === 'true',
      cursor: configEl.dataset.zoomCursor || 'zoom-in',
      mediaCount: parseInt(configEl.dataset.zoomMediaCount) || 0,
      urls: []
    };

    try {
      config.urls = JSON.parse(configEl.dataset.zoomUrls || '[]');
    } catch (e) {
      config.urls = [];
    }

    var gallery = document.getElementById('Gallery-' + sectionId);
    if (!gallery) return;

    var items = gallery.querySelectorAll('.gallery-item');
    items.forEach(function(item, index) {
      var isVideoItem = item.classList.contains('gallery-item--video');

      // Skip hover/lens zoom for video items
      if (!isVideoItem) {
        item.setAttribute('data-zoom-enabled', '');
        item.setAttribute('data-zoom-index', index);

        if (config.type === 'hover') {
          initHoverZoom(item, index);
        } else if (config.type === 'lens') {
          initLensZoom(item, index, sectionId);
        }
      }

      if (config.lightbox) {
        initLightboxTrigger(item, index, sectionId, isVideoItem);
      }
    });

    if (config.lightbox) {
      initLightbox(sectionId);
    }
  }

  // ========================================
  // HOVER ZOOM
  // ========================================
  function initHoverZoom(item, index) {
    item.setAttribute('data-zoom-hover', '');

    // Preload hi-res on first hover
    var preloaded = false;
    var img = item.querySelector('.gallery-img, img');

    item.addEventListener('mouseenter', function() {
      if (!preloaded && config.urls[index]) {
        var preload = new Image();
        preload.src = config.urls[index];
        preloaded = true;
      }
    });

    item.addEventListener('mousemove', function(e) {
      var rect = item.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      item.style.setProperty('--zoom-x', x + '%');
      item.style.setProperty('--zoom-y', y + '%');
      item.style.setProperty('--zoom-scale', config.level);

      // Swap to hi-res src if available
      if (preloaded && img && config.urls[index]) {
        var picture = img.closest('picture');
        if (picture) {
          // For picture elements, update the img src directly
          var imgEl = picture.querySelector('img');
          if (imgEl && !imgEl.dataset.zoomSwapped) {
            imgEl.dataset.originalSrc = imgEl.src;
            imgEl.dataset.originalSrcset = imgEl.srcset || '';
            imgEl.src = config.urls[index];
            imgEl.srcset = '';
            // Hide WebP source to prevent override
            var sources = picture.querySelectorAll('source');
            sources.forEach(function(s) { s.dataset.originalSrcset = s.srcset; s.srcset = ''; });
            imgEl.dataset.zoomSwapped = 'true';
          }
        } else if (!img.dataset.zoomSwapped) {
          img.dataset.originalSrc = img.src;
          img.dataset.originalSrcset = img.srcset || '';
          img.src = config.urls[index];
          img.srcset = '';
          img.dataset.zoomSwapped = 'true';
        }
      }
    });

    item.addEventListener('mouseleave', function() {
      item.style.removeProperty('--zoom-x');
      item.style.removeProperty('--zoom-y');
      item.style.removeProperty('--zoom-scale');

      // Restore original src
      if (img) {
        var picture = img.closest('picture');
        if (picture) {
          var imgEl = picture.querySelector('img');
          if (imgEl && imgEl.dataset.zoomSwapped) {
            imgEl.src = imgEl.dataset.originalSrc;
            imgEl.srcset = imgEl.dataset.originalSrcset;
            var sources = picture.querySelectorAll('source');
            sources.forEach(function(s) { if (s.dataset.originalSrcset) s.srcset = s.dataset.originalSrcset; });
            delete imgEl.dataset.zoomSwapped;
          }
        } else if (img.dataset.zoomSwapped) {
          img.src = img.dataset.originalSrc;
          img.srcset = img.dataset.originalSrcset;
          delete img.dataset.zoomSwapped;
        }
      }
    });

    // Click opens lightbox if enabled
    if (config.lightbox) {
      item.style.cursor = config.cursor;
    }
  }

  // ========================================
  // LENS ZOOM
  // ========================================
  function initLensZoom(item, index, sectionId) {
    item.setAttribute('data-zoom-lens-trigger', '');

    var lens = document.querySelector('[data-zoom-lens="' + sectionId + '"]');
    if (!lens) return;

    var lensImg = lens.querySelector('[data-zoom-lens-img]');
    var preloaded = false;

    item.addEventListener('mouseenter', function() {
      if (!preloaded && config.urls[index]) {
        lensImg.src = config.urls[index];
        preloaded = true;
      }

      // Position lens relative to gallery container
      var container = item.closest('.gallery-container');
      if (container) {
        lens.style.position = 'absolute';
        var containerRect = container.getBoundingClientRect();
        lens.style.left = containerRect.width + 16 + 'px';
        lens.style.top = '0';
      }

      lens.classList.add('is-visible');

      // Set lens image size based on zoom level
      var imgRect = item.getBoundingClientRect();
      lensImg.style.width = (imgRect.width * config.level) + 'px';
      lensImg.style.height = (imgRect.height * config.level) + 'px';
    });

    item.addEventListener('mousemove', function(e) {
      if (!lensImg.src || lensImg.src === '') return;

      var rect = item.getBoundingClientRect();
      var x = (e.clientX - rect.left) / rect.width;
      var y = (e.clientY - rect.top) / rect.height;

      var lensW = lens.offsetWidth;
      var lensH = lens.offsetHeight;
      var imgW = parseFloat(lensImg.style.width);
      var imgH = parseFloat(lensImg.style.height);

      lensImg.style.left = -(x * imgW - lensW / 2) + 'px';
      lensImg.style.top = -(y * imgH - lensH / 2) + 'px';
    });

    item.addEventListener('mouseleave', function() {
      lens.classList.remove('is-visible');
    });
  }

  // ========================================
  // LIGHTBOX
  // ========================================
  function initLightboxTrigger(item, index, sectionId, isVideoItem) {
    if (isVideoItem) {
      // Video items: only open lightbox if clicking outside the play button / active player
      item.addEventListener('click', function(e) {
        if (e.target.closest('.product-video-facade__play')) return;
        if (e.target.closest('.product-video-facade.is-playing')) return;
        e.preventDefault();
        openLightbox(sectionId, index);
      });
      return;
    }

    if (config.type !== 'lens') {
      item.setAttribute('data-zoom-lightbox-trigger', '');
    }

    item.addEventListener('click', function(e) {
      e.preventDefault();
      openLightbox(sectionId, index);
    });
  }

  function initLightbox(sectionId) {
    var lb = document.querySelector('[data-lightbox="' + sectionId + '"]');
    if (!lb) return;

    lightbox = {
      el: lb,
      track: lb.querySelector('[data-lightbox-track]'),
      slides: lb.querySelectorAll('[data-lightbox-slide]'),
      counter: lb.querySelector('[data-lightbox-counter]'),
      sectionId: sectionId
    };

    // Close
    lb.querySelectorAll('[data-lightbox-close]').forEach(function(btn) {
      btn.addEventListener('click', function() { closeLightbox(); });
    });

    // Nav
    var prevBtn = lb.querySelector('[data-lightbox-prev]');
    var nextBtn = lb.querySelector('[data-lightbox-next]');
    if (prevBtn) prevBtn.addEventListener('click', function() { goToSlide(currentSlide - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function() { goToSlide(currentSlide + 1); });

    // Zoom buttons
    var zoomIn = lb.querySelector('[data-lightbox-zoom-in]');
    var zoomOut = lb.querySelector('[data-lightbox-zoom-out]');
    if (zoomIn) zoomIn.addEventListener('click', function() { setLightboxZoom(lbZoomLevel + 0.5); });
    if (zoomOut) zoomOut.addEventListener('click', function() { setLightboxZoom(lbZoomLevel - 0.5); });

    // Keyboard
    lb.addEventListener('keydown', function(e) {
      switch (e.key) {
        case 'Escape': closeLightbox(); break;
        case 'ArrowLeft': goToSlide(currentSlide - 1); break;
        case 'ArrowRight': goToSlide(currentSlide + 1); break;
        case '+': case '=': setLightboxZoom(lbZoomLevel + 0.5); break;
        case '-': setLightboxZoom(lbZoomLevel - 0.5); break;
      }
    });

    // Touch: swipe navigation + pinch-to-zoom
    var track = lightbox.track;
    if (track) {
      track.addEventListener('touchstart', handleTouchStart, { passive: false });
      track.addEventListener('touchmove', handleTouchMove, { passive: false });
      track.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    // Mouse drag for panned images
    lightbox.slides.forEach(function(slide) {
      var img = slide.querySelector('[data-lightbox-img]');
      if (!img) return;

      img.addEventListener('mousedown', function(e) {
        if (lbZoomLevel <= 1) return;
        e.preventDefault();
        isDragging = true;
        dragStart.x = e.clientX - lbPanX;
        dragStart.y = e.clientY - lbPanY;
        img.classList.add('is-dragging');
      });

      img.addEventListener('mousemove', function(e) {
        if (!isDragging) return;
        e.preventDefault();
        lbPanX = e.clientX - dragStart.x;
        lbPanY = e.clientY - dragStart.y;
        applyLightboxTransform(img);
      });

      img.addEventListener('mouseup', function() {
        isDragging = false;
        img.classList.remove('is-dragging');
      });

      img.addEventListener('mouseleave', function() {
        isDragging = false;
        img.classList.remove('is-dragging');
      });

      // Double click to toggle zoom
      img.addEventListener('dblclick', function() {
        if (lbZoomLevel > 1) {
          setLightboxZoom(1);
        } else {
          setLightboxZoom(config.level);
        }
      });
    });
  }

  function openLightbox(sectionId, slideIndex) {
    if (!lightbox.el) return;

    currentSlide = slideIndex || 0;
    lbZoomLevel = 1;
    lbPanX = 0;
    lbPanY = 0;

    // Lazy load images
    lightbox.slides.forEach(function(slide) {
      var img = slide.querySelector('[data-lightbox-img]');
      if (img && img.dataset.src && !img.dataset.loaded) {
        img.src = img.dataset.src;
        img.dataset.loaded = 'true';
      }
    });

    goToSlide(currentSlide, true);
    lightbox.el.classList.add('is-open');
    lightbox.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    lightbox.el.focus();
  }

  function closeLightbox() {
    if (!lightbox.el) return;

    // Pause all videos in lightbox
    if (window.LeanVideo) LeanVideo.pauseAll();

    lightbox.el.classList.remove('is-open');
    lightbox.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Reset zoom on all slides
    lightbox.slides.forEach(function(slide) {
      var img = slide.querySelector('[data-lightbox-img]');
      if (img) {
        img.style.transform = '';
        img.classList.remove('is-zoomed', 'is-dragging');
      }
    });
    lbZoomLevel = 1;
    lbPanX = 0;
    lbPanY = 0;
  }

  function goToSlide(index, instant) {
    if (!lightbox.slides.length) return;

    // Pause videos when changing slides
    if (window.LeanVideo) LeanVideo.pauseAll();

    var total = lightbox.slides.length;
    currentSlide = ((index % total) + total) % total;

    // Reset zoom when changing slides
    if (!instant) {
      setLightboxZoom(1);
    }

    lightbox.slides.forEach(function(slide, i) {
      var offset = (i - currentSlide) * 100;
      slide.style.transform = 'translateX(' + offset + '%)';
      slide.style.transition = instant ? 'none' : 'transform 0.3s ease';
    });

    if (lightbox.counter) {
      lightbox.counter.textContent = (currentSlide + 1) + ' / ' + total;
    }
  }

  function setLightboxZoom(level) {
    lbZoomLevel = Math.min(4, Math.max(1, level));
    lbPanX = 0;
    lbPanY = 0;

    var slide = lightbox.slides[currentSlide];
    if (!slide) return;
    var img = slide.querySelector('[data-lightbox-img]');
    if (!img) return;

    // Load full-res on zoom > 2x
    if (lbZoomLevel > 2 && img.dataset.fullSrc && !img.dataset.fullLoaded) {
      img.src = img.dataset.fullSrc;
      img.dataset.fullLoaded = 'true';
    }

    applyLightboxTransform(img);
    img.classList.toggle('is-zoomed', lbZoomLevel > 1);
  }

  function applyLightboxTransform(img) {
    img.style.transform = 'scale(' + lbZoomLevel + ') translate(' + (lbPanX / lbZoomLevel) + 'px, ' + (lbPanY / lbZoomLevel) + 'px)';
  }

  // ========================================
  // TOUCH HANDLING
  // ========================================
  function handleTouchStart(e) {
    if (e.touches.length === 2) {
      // Pinch start
      e.preventDefault();
      touchStartDist = getTouchDistance(e.touches);
      touchStartZoom = lbZoomLevel;
    } else if (e.touches.length === 1) {
      swipeStartX = e.touches[0].clientX;

      if (lbZoomLevel > 1) {
        // Pan start
        e.preventDefault();
        isDragging = true;
        dragStart.x = e.touches[0].clientX - lbPanX;
        dragStart.y = e.touches[0].clientY - lbPanY;
      }
    }
  }

  function handleTouchMove(e) {
    if (e.touches.length === 2) {
      // Pinch zoom
      e.preventDefault();
      var dist = getTouchDistance(e.touches);
      var scale = dist / touchStartDist;
      setLightboxZoom(touchStartZoom * scale);
    } else if (e.touches.length === 1 && isDragging && lbZoomLevel > 1) {
      // Pan
      e.preventDefault();
      lbPanX = e.touches[0].clientX - dragStart.x;
      lbPanY = e.touches[0].clientY - dragStart.y;

      var slide = lightbox.slides[currentSlide];
      if (slide) {
        var img = slide.querySelector('[data-lightbox-img]');
        if (img) applyLightboxTransform(img);
      }
    }
  }

  function handleTouchEnd(e) {
    if (isDragging) {
      isDragging = false;
      return;
    }

    // Swipe navigation (only when not zoomed)
    if (lbZoomLevel <= 1 && e.changedTouches.length === 1) {
      var deltaX = e.changedTouches[0].clientX - swipeStartX;
      if (Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          goToSlide(currentSlide - 1);
        } else {
          goToSlide(currentSlide + 1);
        }
      }
    }
  }

  function getTouchDistance(touches) {
    var dx = touches[0].clientX - touches[1].clientX;
    var dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  return { init: init, openLightbox: openLightbox, closeLightbox: closeLightbox };
})();

window.LeanZoom = LeanZoom;
